// ===== AGB VARIANT MODULE =====
AGB.define('variant', ['core', 'store'], function (Core, Store) {
  const { dom, scheduler, bus, utils, config, ErrorHandler } = Core;

  function getLocalizedProductJsonUrl(handle) {
    const root = String(window?.Shopify?.routes?.root || '/').replace(/\/?$/, '/');
    return `${root}products/${encodeURIComponent(handle)}.js`;
  }

  // Track the previously-resolved variant for theme sync
  let _prevResolvedVariant = null;

  // ---- helpers -------------------------------------------------------------
  // Check if product is a "free item" (1 variant, 1 option with "Default Title")
  function isFreeItem(product) {
    if (!product) return false;
    const variants = Array.isArray(product.variants) ? product.variants : [];
    const options = Array.isArray(product.options) ? product.options : [];

    // Must have exactly 1 variant and 1 option
    if (variants.length !== 1 || options.length !== 1) return false;

    const variant = variants[0];
    // Check if the single variant has "Default Title" or similar
    const option1 = String(variant.option1 || variant.options?.[0] || '').trim();
    const isDefaultTitle = /default\s*title/i.test(option1) || option1 === '';

    // Also check if price is 0 (free item indicator)
    const isFree = Number(variant.price || 0) === 0;

    return isDefaultTitle || isFree;
  }

  // Auto-select variant for free items
  function autoSelectFreeItemVariant() {
    const state = Store.getState();
    const product = state?.product;
    if (!isFreeItem(product)) return false;

    const variant = product.variants[0];
    if (!variant || !variant.available) return false;

    const form = dom.form;
    if (!form) return false;

    // Set hidden variant ID input FIRST (most critical - this is what Shopify needs for form submission)
    // This works even when variant picker doesn't render (has_only_default_variant = true)
    const hiddenIdInput = dom.hiddenIdInput;
    if (hiddenIdInput) {
      hiddenIdInput.value = variant.id;
    }

    // Try to set option inputs if they exist (variant picker might not render for free items)
    // This is optional - don't fail if inputs don't exist
    const optionCount = Array.isArray(product.options) ? product.options.length : 0;
    for (let i = 0; i < optionCount; i++) {
      const optionName = `option${i + 1}`;
      const optionValue = variant.options?.[i] || variant[`option${i + 1}`] || '';

      if (optionValue) {
        // Try input
        const input = form.querySelector(`input[name="${optionName}"]`);
        if (input) {
          input.value = optionValue;
          // Trigger change event to notify variant picker
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Try select
        const select = form.querySelector(`select[name="${optionName}"]`);
        if (select) {
          select.value = optionValue;
          select.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // Try radio button
        const radio = form.querySelector(`input[type="radio"][name="${optionName}"][value="${optionValue}"]`);
        if (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    // Mark all options as chosen by user for free items (even if inputs don't exist)
    Store.mutate(s => {
      for (let i = 0; i < optionCount; i++) {
        s.userInteractedOptions.add(i);
      }
    });

    // Trigger variant resolution (this will enable continue button)
    // For free items, resolveVariant will handle the case where no option inputs exist
    resolveVariant();
    return true;
  }

  function optionCount() {
    const p = Store.getState()?.product;
    const count = Array.isArray(p?.options) ? p.options.length : 0;
    return Math.max(0, count); // Ensure non-negative
  }
  function optionName(i) {
    const p = Store.getState()?.product;
    return (p?.options?.[i]?.name || p?.options?.[i] || '').toString();
  }
  function findColorOptionIndex() {
    const count = optionCount();
    if (count === 0) return -1; // Early exit for products with no options

    for (let i = 0; i < count; i++) {
      if (/(color|colour|pattern|camouflage|camo)/i.test(optionName(i))) return i;
    }
    return 0; // Fallback to first option if no color-like option found
  }

  // ---- bundle-aware helpers --------------------------------------------------
  // Returns the fixedColor from the bundle spec if the current product is a
  // fixed-color bundle (e.g. "sub-zero-ranger-green-bundle"), otherwise null.
  function getFixedBundleColor() {
    let Bundles = null;
    try { Bundles = AGB.require('bundles'); } catch (_) { }
    const handle = Store.getState()?.product?.handle;
    if (!handle || !Bundles?.BUNDLE_DATA) return null;
    return Bundles.BUNDLE_DATA[handle]?.fixedColor || null;
  }

  // Returns bundle-aware { color, size } from a variant.
  // Fixed-color bundles: option1 is SIZE, color comes from spec.fixedColor.
  // Everything else: option1 is color, option2 is size.
  function bundleAwareColorSize(variant) {
    if (!variant) return { color: '', size: '' };
    const fixed = getFixedBundleColor();
    if (fixed) return { color: fixed, size: variant.option1 || '' };
    return { color: variant.option1 || '', size: variant.option2 || '' };
  }

  // True when the current product is any kind of bundle.
  function isCurrentProductBundle() {
    let Bundles = null;
    try { Bundles = AGB.require('bundles'); } catch (_) { }
    const handle = Store.getState()?.product?.handle;
    return !!(handle && Bundles?.isBundle && Bundles.isBundle(handle));
  }
  // Core.require does not exist (the module registry is AGB.require) - so every
  // `Core.require?.('suggest')` here was a silent no-op: the deterministic
  // accessory re-colour on a base colour change never ran, and only the current
  // card followed the new colour (measured on k19, 2026-09-02). agb-suggest is
  // lazy-loaded with the UI pack, so it may legitimately be absent - hence the
  // try/catch, not a throw.
  function requireSuggest() {
    try { return AGB.require('suggest'); } catch (_) { return null; }
  }
  function readFormOption(i) {
    const form = dom.form;
    if (!form) return '';
    // 1) custom swatch (aria-pressed)
    const sw = form.querySelector(`.custom-swatch[data-option-index="${i}"][aria-pressed="true"]`);
    if (sw && sw instanceof HTMLElement && sw.dataset.value) return sw.dataset.value;
    // 2) radio (checked)
    const radio = form.querySelector(`#step1 input[name="option${i + 1}"]:checked`);
    if (radio instanceof HTMLInputElement && radio.value) return radio.value;
    // 3) select dropdown
    const sel = form.querySelector(`#step1 select[name="option${i + 1}"]`);
    if (sel instanceof HTMLSelectElement && sel.value) return sel.value;
    // 3) hidden/normal input
    const hid = form.querySelector(`#step1 input[name="option${i + 1}"]`);
    if (hid instanceof HTMLInputElement && hid.value) return hid.value;
    return '';
  }
  function getSelectedOptionValues() {
    const n = optionCount();
    return Array.from({ length: n }, (_, i) => readFormOption(i));
  }
  function findMatchingVariant(selected) {
    const product = Store.getState()?.product;
    if (!Array.isArray(product?.variants)) return null;
    return product.variants.find(v =>
      Array.isArray(v.options) && v.options.every((val, i) => String(val) === String(selected[i] || ''))
    ) || null;
  }
  function setHiddenId(variant) {
    let idEl = dom.hiddenIdInput;
    if (!idEl && dom.form) {
      idEl = document.createElement('input');
      idEl.type = 'hidden';
      idEl.name = 'id';
      dom.form.appendChild(idEl);
      dom.hiddenIdInput = idEl;
    }
    // Only update when we have a concrete variant; never clear existing id
    if (idEl && variant) idEl.value = String(variant.id);
  }
  function setContinueEnabled(can) {
    if (!dom.continueBtn) return;
    dom.continueBtn.hidden = false;
    dom.continueBtn.disabled = !can;
  }
  function setStep2TabEnabled(can) {
    const tab = document.getElementById('tab-step2');
    if (!tab) return;
    tab.removeAttribute('tabindex');
    tab.setAttribute('aria-selected', 'false');
    if (can) tab.removeAttribute('aria-disabled');
    else tab.setAttribute('aria-disabled', 'true');
  }
  // Drop-in replacement
  function dispatchVariantChange(variant, previousVariant) {
    try {
      const formId = window.AGB_BOOT?.productFormId || window.AGB_FORM_ID;
      const form = document.getElementById(formId);
      const target = form || document;

      if (!variant) return;

      // Normalize variant so theme.js & listeners always have fields they expect
      const safeVariant = {
        id: variant.id ?? null,
        title: variant.title || '',
        available: !!variant.available,
        price: variant.price || 0,
        compare_at_price: variant.compare_at_price || 0,
        sku: variant.sku || '',
        option1: variant.option1 || '',
        option2: variant.option2 || '',
        option3: variant.option3 || '',
        featured_media: variant.featured_media || variant.featured_image || (variant.media && variant.media[0]) || null,
        featured_image: variant.featured_image || null,
        media: variant.media || (variant.featured_image ? [variant.featured_image] : []),
        options: Array.isArray(variant.options) ? variant.options : [
          variant.option1 || '',
          variant.option2 || '',
          variant.option3 || ''
        ].filter(Boolean),
        ...variant
      };

      const product = Store.getState().product || null;
      const safeProduct = product ? {
        ...product,
        media: product.media || (product.images ? product.images.map(src => ({ src })) : []),
        featured_media: product.featured_media || product.featured_image || null
      } : null;

      // Always emit the theme-standard event for UI. Prestige's
      // ProductGallery.onVariantChange_fn reads previousVariant["featured_media"]
      // without null-guarding previousVariant (only ?. on the inner id lookup),
      // so passing null crashes on the first dispatch. Use {} so the lookup
      // returns undefined and the inequality short-circuits cleanly.
      try {
        target.dispatchEvent(new CustomEvent('variant:change', {
          bubbles: true,
          detail: { product: safeProduct, variant: safeVariant, previousVariant: previousVariant || {} }
        }));
      } catch (_) { }

      // ---------------- Bundle integration (idempotent + guarded) ----------------
      // Ensure we have a bundle system and this product is actually a bundle
      let Bundles = null;
      try { Bundles = AGB.require('bundles'); } catch (_) { }
      const isBundle = !!(safeProduct && Bundles?.isBundle && Bundles.isBundle(safeProduct.handle));
      if (!isBundle) return;

      // Run only when the "selection signature" truly changed
      dispatchVariantChange.__lastBundleSig = dispatchVariantChange.__lastBundleSig || null;
      const optSig = (safeVariant.options || []).join('|');
      const sig = `${safeProduct.handle}:${safeVariant.id}:${optSig}`;
      if (dispatchVariantChange.__lastBundleSig === sig) return;
      dispatchVariantChange.__lastBundleSig = sig;

      if (window.AGB_DEBUG_BUNDLES) {
        console.log('[Bundle] Processing bundle product:', safeProduct.handle);
      }

      Promise.resolve()
        .then(() => Bundles.resolveBundleFromVariant(safeProduct, safeVariant))
        .then((result) => {
          if (result && result.baseItem) {
            if (window.AGB_DEBUG_BUNDLES) {
              console.log('[Bundle] Applying bundle result:', result);
            }
            Bundles.applyBundleToStore(result); // re-entrancy guard prevents loops
          }
        })
        .catch((error) => {
          console.warn('[Bundle] resolve/apply failed', error);
        });

    } catch (e) { /* swallow – keep UX resilient */ }
  }


  // Public helper needed by Suggest
  function pickVariantByColor(color) {
    const p = Store.getState()?.product;
    if (!p?.variants || !color) return null;
    // Normalize for case-insensitive comparison (handles "Black" vs "black" issues)
    const normalize = (s) => String(s || '').trim();
    const normalizedColor = normalize(color);
    return p.variants.find(v => normalize(v.option1) === normalizedColor && v.available)
      || p.variants.find(v => normalize(v.option1) === normalizedColor)
      || null;
  }

  // ---- shared color fallback map -------------------------------------------
  // const COLOR_FALLBACK_MAP = {
  //   'Multicam': ['Coyote Tan', 'Ranger Green', 'Coyote Brown'],
  //   'Multicam Black': ['Black', 'Ranger Green'],
  //   'Ranger Green': ['Multicam', 'Black', 'Coyote Tan'],
  //   'Black': ['Multicam Black', 'Ranger Green'],
  //   'M81 Woodland': ['Multicam', 'Ranger Green'],
  //   'Coyote Brown': ['Coyote Tan', 'Multicam'],
  //   'Coyote Tan': ['Coyote Brown', 'Multicam'],
  // };

  const colorFallbackMap = Store.COLOR_FALLBACK_MAP;

  // Update the current (pending) suggestion to match the base color, then re-render the card
  async function updateCurrentSuggestionToColor(newBaseColor) {
    const st = Store.getState();
    if (!st?.pendingSuggestion?.handle || !newBaseColor) return;

    // Try Suggest cache first (fast path)
    let product = null;
    try {
      const Suggest = requireSuggest();
      if (Suggest?.fetchProductData) {
        product = await Promise.resolve(Suggest.fetchProductData(st.pendingSuggestion.handle));
      }
    } catch { }

    // If cache miss, fetch once (rare)
    if (!product) {
      try {
        const res = await fetch(getLocalizedProductJsonUrl(st.pendingSuggestion.handle));
        if (res.ok) product = await res.json();
      } catch { }
    }
    if (!product?.variants?.length) return;

    // Choose best variant by color with limited fallbacks
    const pick = chooseVariantForColor(product.variants, newBaseColor);
    if (!pick) return;

    // Update preview color and re-render the current card
    Store.mutate(s => { if (s.pendingSuggestion) s.pendingSuggestion.color = pick.option1; });
    Core.scheduler.mark('canvas');

    try {
      const Suggest = requireSuggest();
      Suggest?.renderNext?.({ noAutoHop: true }); // redraw current suggestion card to reflect new color
    } catch { }
  }

  // Update the currently visible suggestion card's image/price in-place
  async function updateCurrentCardForColor(newColor) {
    const { suggestionsList } = Core.dom || {};
    const card = suggestionsList?.querySelector('.suggestion-card');
    if (!card || !newColor) return;

    const handle = card.dataset.handle;
    if (!handle) return;

    // cache-first
    let product = null;
    try { product = await Promise.resolve(requireSuggest()?.fetchProductData?.(handle)); } catch { }
    if (!product?.variants?.length) {
      try {
        const res = await fetch(getLocalizedProductJsonUrl(handle));
        if (res.ok) product = await res.json();
      } catch { }
    }
    if (!product?.variants?.length) return;

    const pick = chooseVariantForColor(product.variants, newColor);
    if (!pick) return;

    // Update DOM
    const imgEl = card.querySelector('.suggestion-card-img');
    const raw = pick.featured_image?.src || (product.images?.[0] || '');
    const url = raw ? raw + (raw.includes('?') ? '&' : '?') + 'width=100' : '';
    if (imgEl && url) imgEl.src = url;

    // Update price
    const priceEl = card.querySelector('.agb-item-price') || (() => {
      const el = document.createElement('span');
      el.className = 'agb-item-price';
      const title = card.querySelector('.suggestion-card-title');
      title?.insertAdjacentElement('afterend', el);
      return el;
    })();
    const qty = Number(card.dataset.qty || 1);
    Core.utils.renderCardPrice({
      container: priceEl,
      qty,
      price: (Number(pick.price) || 0) / 100,
      compareAt: (Number(pick.compare_at_price) || 0) / 100
    });

    // Keep dataset in sync
    card.dataset.variantId = String(pick.id);
    card.dataset.color = pick.option1;
  }

  // ---- public: resolve -----------------------------------------------------
  function resolveVariant() {
    const state = Store.getState();
    const selected = getSelectedOptionValues();
    const allChosen = selected.length === optionCount() && selected.every(Boolean);
    // Resolve variant debug info

    let variant = allChosen ? findMatchingVariant(selected) : null;

    // For free items, if no variant found from options (because variant picker doesn't render),
    // check if hidden ID is set and use the product's first variant directly
    const isFree = isFreeItem(state.product);
    if (isFree && !variant) {
      const hiddenIdInput = dom.hiddenIdInput;
      if (hiddenIdInput && hiddenIdInput.value) {
        // Hidden ID is set, use the first variant (free items only have one)
        variant = state.product?.variants?.[0] || null;
      }
    }

    setHiddenId(variant);
    // Require user interaction with ALL options, not just auto-selected values
    // Exception: free items (1 variant, 1 option) don't require user interaction
    const userChoseAllOptions = isFree ? true : nav._allOptionsChosenByUser();
    const canContinue = !!(variant && variant.available && userChoseAllOptions);
    // Can continue debug info
    setContinueEnabled(canContinue);
    setStep2TabEnabled(canContinue);

    // keep common color & a lightweight “selected variant” mirror in state
    const colorIdx = findColorOptionIndex();
    const fixedColor = getFixedBundleColor();
    const color = fixedColor || (colorIdx > -1 ? (selected[colorIdx] || null) : null);
    const size  = fixedColor ? (selected[0] || null) : (selected[1] || null);

    Store.mutate(s => {
      s.commonVariantColor.color = color;
      s._selectedVariant = variant ? {
        id: variant.id,
        sku: variant.sku,
        available: !!variant.available,
        price: variant.price,
        option1: variant.option1,
        option2: variant.option2,
        featured_image: variant.featured_image || null
      } : null;
    });

    // tell theme/gallery & sticky — only when we have a real variant
    if (variant) {
      const previousVariant = _prevResolvedVariant;
      dispatchVariantChange(variant, previousVariant);
      _prevResolvedVariant = variant;
    }

    // If there’s already a base item, keep it in sync
    // Replace the existing base item update section with:
    if (state.baseItem && variant) {
      const previousColor = state.baseItem.color;
      const { color: varColor, size: varSize } = bundleAwareColorSize(variant);

      Store.mutate(s => {
        s.baseItem.id = variant.id;
        s.baseItem.sku = variant.sku;
        s.baseItem.color = varColor;
        s.baseItem.size = varSize;
        s.baseItem.price = (Number(variant.price) / 100).toFixed(2);
        s.baseItem.available = !!variant.available;
        s.baseItem.image = variant.featured_image?.src || s.product?.featured_image;
      });

      // Emit baseItemChanged to trigger sync.
      // For fixed-color bundles varColor is constant so this never fires
      // (no spurious re-render). For non-fixed bundles / normal products it
      // fires exactly once when the colour genuinely changes.
      if (previousColor !== varColor) {
        bus.emit('baseItemChanged');
      }

      scheduler.mark('ui'); scheduler.mark('pricing'); scheduler.mark('sticky'); scheduler.mark('canvas');
      ErrorHandler?.safeSync(() => window.DEBUG_LOGGER?.baseItemEdit?.(color, size), null, 'DEBUG_LOGGER.baseItemEdit');
    }

    // INTERNAL PROGRESSION: Only advance when strict gate passes (all options chosen by user & variant available)
    if (!state.proceededToStep2 && state.builderStage === 1 && nav.gateStep2Strict()) {
      // Strict gate passed
      // Set base item from current selection if not already set
      if (variant && state.product && !state.baseItem) {
        const qtyInput = dom.form?.querySelector('input[name="quantity"]');
        const qty = parseInt(qtyInput?.value || 1, 10);
        const variantPriceUnits = Number(variant.price) / 100;

        const { color: initColor, size: initSize } = bundleAwareColorSize(variant);
        const newBaseItem = {
          id: variant.id,
          handle: state.product.handle,
          title: state.product.title,
          sku: variant.sku,
          color: initColor,
          size: initSize,
          qty,
          price: variantPriceUnits.toFixed(2),
          available: variant.available,
          image: variant.featured_image?.src || state.product.featured_image
        };

        // Set base item
        Store.setBaseItem(newBaseItem);
        scheduler.mark('ui'); scheduler.mark('pricing'); scheduler.mark('sticky'); scheduler.mark('canvas');
      }

      // Automatically progress to stage 2
      setTimeout(() => {
        // Calling nav.go(2)
        nav.go(2);
      }, 100);
    }

  }

  // ---- public: sync base with current hidden id ----------------------------
  function syncBaseItemVariant() {
    const st = Store.getState();
    if (!st.baseItem || !dom.form) return;
    const id = dom.form.querySelector('input[name="id"]')?.value;
    if (!id) return;

    const v = st.product?.variants?.find(x => String(x.id) === String(id));
    if (!v) return;

    const previousColor = st.baseItem.color;
    // Normalize for case-insensitive comparison (handles "Black" vs "black" issues)
    const normalize = (s) => String(s || '').trim();
    const { color: syncColor, size: syncSize } = bundleAwareColorSize(v);

    Store.mutate(s => {
      s.baseItem.id = v.id;
      s.baseItem.sku = v.sku;
      s.baseItem.color = syncColor;
      s.baseItem.size = syncSize;
      s.baseItem.price = (Number(v.price) / 100).toFixed(2);
      s.baseItem.available = !!v.available;
      s.baseItem.image = v.featured_image?.src || s.product?.featured_image;
      s.commonVariantColor.color = syncColor || s.commonVariantColor.color;
    });

    // Sync all (accepted + pending suggestion + suggestion card re-render)
    // Use case-insensitive comparison to handle "Black" vs "black" variations
    if (normalize(previousColor) !== normalize(syncColor)) {
      syncAllToBaseColor?.(syncColor);
    }

    scheduler.mark('ui');
    scheduler.mark('canvas');
    updateCurrentCardForColor(syncColor);
    updateCurrentSuggestionToColor(syncColor);
  }

  // Pick best available variant given preferred color with limited fallbacks
  // Multicam <-> Coyote Tan interchangeable (Dorian 2026-07-13): item not offered
  // in the requested colour but offered in the other => use the other. Both
  // directions, EXISTENCE-based. Panel flag `multicamTanSwap` (default ON).
  function _mcTanRemap(variants, targetColor, colorOf) {
    try {
      const gs = window.__AGB_GEAR_SUGG;
      if (gs && gs.raw && gs.raw.multicamTanSwap === false) return targetColor;
      const equiv = (targetColor === 'Multicam') ? 'Coyote Tan'
                  : (targetColor === 'Coyote Tan') ? 'Multicam' : null;
      if (!equiv || !Array.isArray(variants)) return targetColor;
      const has = (c) => variants.some(v => colorOf(v) === c);
      if (has(targetColor)) return targetColor;
      return has(equiv) ? equiv : targetColor;
    } catch (e) { return targetColor; }
  }

  function chooseVariantForColor(variants, desiredColor) {
    if (!Array.isArray(variants) || !desiredColor) return null;
    desiredColor = _mcTanRemap(variants, desiredColor, v => v.option1);

    // Normalize for case-insensitive comparison (handles "Black" vs "black" issues)
    const normalize = (s) => String(s || '').trim();
    const normalizedDesired = normalize(desiredColor);

    // 1) exact available (case-insensitive)
    let v = variants.find(v => normalize(v.option1) === normalizedDesired && v.available);
    if (v) return v;

    // 2) exact (even if not available) – do not use if unavailable; skip
    // 3) try fallback colors if defined and available
    const fallbacks = colorFallbackMap[desiredColor];
    if (Array.isArray(fallbacks)) {
      for (const fallbackColor of fallbacks) {
        v = variants.find(v => normalize(v.option1) === normalize(fallbackColor) && v.available);
        if (v) return v;
      }
    }

    // 4) nothing suitable
    return null;
  }

  // Track sync generation to prevent stale updates
  let syncGeneration = 0;

  // Sync accepted items and current suggestion to base color (fast)
  async function syncAllToBaseColor(newBaseColor) {
    const state = Store.getState();
    if (!newBaseColor) return;

    // Increment generation counter
    const currentGeneration = ++syncGeneration;

    // ---- Accepted items: ensure every item is updated to the new color (with stock check) ----
    let acceptedChanged = false;

    // Build product cache for handles that lack embedded variants
    const handlesNeedingProduct = new Set();
    for (const it of (state.acceptedItems || [])) {
      if (!Array.isArray(it.variants) || it.variants.length === 0) handlesNeedingProduct.add(it.handle);
    }

    const productByHandle = Object.create(null);
    if (handlesNeedingProduct.size > 0) {
      const Suggest = requireSuggest();
      const fetchers = Array.from(handlesNeedingProduct).map(async (h) => {
        let prod = null;
        try { prod = await Promise.resolve(Suggest?.fetchProductData?.(h)); } catch { }
        if (!prod) {
          try {
            const res = await fetch(getLocalizedProductJsonUrl(h));
            if (res.ok) prod = await res.json();
          } catch { }
        }
        if (prod && Array.isArray(prod.variants)) productByHandle[h] = prod;
      });
      await Promise.all(fetchers);
    }

    // Discard if newer sync already started
    if (currentGeneration !== syncGeneration) {
      return; // Stale data, abort
    }

    const updatedAccepted = (state.acceptedItems || []).map(item => {
      // Prefer embedded variants; fallback to fetched product variants
      const variants = Array.isArray(item.variants) && item.variants.length
        ? item.variants
        : (productByHandle[item.handle]?.variants || null);

      if (!variants) return item; // cannot resolve; leave unchanged

      const target = chooseVariantForColor(variants, newBaseColor);
      // If no in-stock match (even after fallback), keep original item
      if (!target) return item;

      // Skip if already on the same variant id
      if (String(target.id) === String(item.variantId)) return item;

      acceptedChanged = true;
      return {
        ...item,
        variantId: target.id,
        id: target.id,
        color: target.option1,
        variantColor: target.option1,
        price: (Number(target.price) / 100).toFixed(2),
        title: `${(item.title || '').split(' (')[0]} (${target.title})`
      };
    });

    if (acceptedChanged) {
      Store.mutate(s => { s.acceptedItems = updatedAccepted; });
      try { window.acceptedItems = updatedAccepted; } catch { }
      scheduler.mark('ui'); scheduler.mark('sticky'); scheduler.mark('pricing'); scheduler.mark('canvas');
      Core.bus.emit('acceptedItemsChanged');
    }

    // DETERMINISTIC RE-COLOUR (Dorian 2026-07-13 — fixes "colour doesn't update
    // until I click / only some accessories update"). The suggestion cards were
    // recoloured ONLY by the debounced+conditional acceptedItemsChanged handler,
    // which under the async product-fetch path or rapid colour switches could be
    // delayed, skipped (acceptedChanged===false) or lost to a generation abort —
    // leaving cards on the OLD colour until a manual click forced a fresh pass.
    // Now that this sync is the authoritative latest one (generation still ==),
    // push a direct in-place card refresh + canvas repaint. updateCardStates is
    // light and idempotent, and reads the just-mutated state, so every accessory
    // settles on the new base colour immediately and consistently.
    if (currentGeneration === syncGeneration) {
      try { requireSuggest()?.updateCardStates?.(); } catch { }
      scheduler.mark('canvas');
    }

    // ---- Current suggestion (pendingSuggestion) ----
    const ps = state.pendingSuggestion;
    if (!ps?.handle) return;

    // Try Suggest cache first
    let product = null;
    try {
      const Suggest = requireSuggest();
      if (Suggest?.fetchProductData) {
        // normalize sync/async return to a promise
        product = await Promise.resolve(Suggest.fetchProductData(ps.handle));
      }
    } catch { }

    // If cache miss, fetch once (rare)
    if (!product) {
      try {
        const res = await fetch(getLocalizedProductJsonUrl(ps.handle));
        if (res.ok) product = await res.json();
      } catch { }
    }

    if (!product?.variants?.length) return;

    const pick = chooseVariantForColor(product.variants, newBaseColor);
    if (!pick) return;

    // Update color for preview (agb-suggest baseItemChanged listener will handle re-render)
    Store.mutate(s => { if (s.pendingSuggestion) s.pendingSuggestion.color = pick.option1; });
    scheduler.mark('canvas');
  }

  // Auto-sync when the base item changes (e.g., color change)
  const unsubscribeBaseItemChanged = bus.on('baseItemChanged', () => {
    const st = Store.getState();
    const color = st.baseItem?.color;
    if (!color) return;
    // Orchestrate updates: accepted items, pending suggestion, card re-render, canvas
    syncAllToBaseColor(color);
  });

  // Cleanup function
  function cleanup() {
    if (unsubscribeBaseItemChanged) {
      unsubscribeBaseItemChanged();
    }
  }

  // ---- nav / gating --------------------------------------------------------
  const nav = {
    isStackedLayout() {
      const bp = Number(Core?.config?.MOBILE_STACK_BREAKPOINT ?? 1000);
      return window.innerWidth < bp;
    },

    // Strict completeness: every option has a value AND the user touched all options
    _allOptionsChosenByUser() {
      const n = optionCount();
      if (n === 0) return false;

      const selected = getSelectedOptionValues();
      const allFilled = selected.length === n && selected.every(Boolean);

      return allFilled;
    },

    // Match the current selection to a real, available variant
    _getSelectedVariant() {
      const st = Store.getState();
      const id = Core.dom.form?.querySelector('input[name="id"]')?.value;
      if (id) {
        return st.product?.variants?.find(v => String(v.id) === String(id)) || null;
      }
      // Fallback to a pure match from current selections
      return findMatchingVariant(getSelectedOptionValues());
    },

    _variantIsAvailable(v) {
      return !!(v && v.available);
    },

    // Public: strict gate for Step 2
    gateStep2Strict() {
      const okByUser = this._allOptionsChosenByUser();
      const v = this._getSelectedVariant();
      const ok = okByUser && this._variantIsAvailable(v);
      // Gate step 2 strict debug
      return ok;
    },

    // Ensure baseItem is created/synced before step 2
    _ensureBaseItemFromSelection() {
      const st = Store.getState();
      if (st.baseItem) return true;

      const v = this._getSelectedVariant();
      if (!v) return false;

      const qty = parseInt(Core.dom.form?.querySelector('input[name="quantity"]')?.value || 1, 10);
      const { color: ensColor, size: ensSize } = bundleAwareColorSize(v);
      Store.setBaseItem({
        id: v.id,
        handle: st.product?.handle,
        title: st.product?.title,
        sku: v.sku,
        color: ensColor,
        size: ensSize,
        qty,
        price: (Number(v.price) / 100).toFixed(2),
        available: !!v.available,
        image: v.featured_image?.src || st.product?.featured_image
      });
      return true;
    },

    async go(step) {
      const st = Store.getState();
      if (Number(step) !== 2) return;

      // Strict gate
      if (!this.gateStep2Strict()) {
        Core.utils?.toast?.('Please choose color & size first.');
        return;
      }
      if (!this._ensureBaseItemFromSelection()) return;

      // INTERNAL progression: state is the source of truth
      // Progressing to stage 2
      Store.setBuilderStage(2);

      // try {
      //   const Panels = Core.require?.('panels');
      //   if (Panels?.manageCanvasGalleryVisibility) {
      //     Panels.manageCanvasGalleryVisibility(true);
      //     // Canvas shown, gallery hidden
      //   }
      // } catch (e) {
      //   // Failed to show canvas
      // }

      if (!st.proceededToStep2) {
        Store.mutate(s => { s.proceededToStep2 = true; });
        document.documentElement.setAttribute('data-agb-step2', '1');
        bus.emit('agb:step2');     // triggers lazy-loading & mobile rail opening
        scheduler.mark('sticky');  // keep sticky in sync with stage change
      }

      // Open Step 2 panel - let agb-panels handle mobile rail opening via agb:step2 event
      if (Core.dom.step2) {
        // Always unhide step2 first to ensure it's available
        Core.dom.step2.hidden = false;

        // For desktop, show panel normally
        // For mobile, the agb:step2 event will trigger agb-panels to handle rail opening
        if (!this.isStackedLayout()) {
          Core.dom.step2.classList.add('is-open');
        }
      }
    }
  };


  return {
    resolveVariant,
    syncBaseItemVariant,
    syncAllToBaseColor,
    nav,
    findColorOptionIndex,
    getCurrentlySelectedColor() {
      // Fixed-color bundles: color is dictated by the spec, not by form options
      const fixed = getFixedBundleColor();
      if (fixed) return fixed;
      const idx = findColorOptionIndex();
      if (idx < 0) return null;
      return getSelectedOptionValues()[idx] || null;
    },
    pickVariantByColor,
    updateCurrentCardForColor,
    autoSelectFreeItemVariant,
    isFreeItem,
    cleanup
  };
});




// // ===== AGB VARIANT MODULE =====
// AGB.define('variant', ['core', 'store'], function (Core, Store) {
//   const { dom, scheduler, bus, utils, config, ErrorHandler } = Core;

//   // Track the previously-resolved variant for theme sync
//   let _prevResolvedVariant = null;

//   // ---- helpers -------------------------------------------------------------
//   function optionCount() {
//     const p = Store.getState()?.product;
//     return Array.isArray(p?.options) ? p.options.length : 0;
//   }
//   function optionName(i) {
//     const p = Store.getState()?.product;
//     return (p?.options?.[i]?.name || p?.options?.[i] || '').toString();
//   }
//   function findColorOptionIndex() {
//     for (let i = 0; i < optionCount(); i++) {
//       if (/(color|colour|pattern|camouflage|camo)/i.test(optionName(i))) return i;
//     }
//     return -1;
//   }
//   function readFormOption(i) {
//     const form = dom.form;
//     if (!form) return '';
//     // 1) custom swatch (aria-pressed)
//     const sw = form.querySelector(`.custom-swatch[data-option-index="${i}"][aria-pressed="true"]`);
//     if (sw && sw instanceof HTMLElement && sw.dataset.value) return sw.dataset.value;
//     // 2) radio (checked)
//     const radio = form.querySelector(`#step1 input[name="option${i + 1}"]:checked`);
//     if (radio instanceof HTMLInputElement && radio.value) return radio.value;
//     // 3) hidden/normal input
//     const hid = form.querySelector(`#step1 input[name="option${i + 1}"]`);
//     if (hid instanceof HTMLInputElement && hid.value) return hid.value;
//     return '';
//   }
//   function getSelectedOptionValues() {
//     const n = optionCount();
//     return Array.from({ length: n }, (_, i) => readFormOption(i));
//   }
//   function findMatchingVariant(selected) {
//     const product = Store.getState()?.product;
//     if (!Array.isArray(product?.variants)) return null;
//     return product.variants.find(v =>
//       Array.isArray(v.options) && v.options.every((val, i) => String(val) === String(selected[i] || ''))
//     ) || null;
//   }
//   function setHiddenId(variant) {
//     let idEl = dom.hiddenIdInput;
//     if (!idEl && dom.form) {
//       idEl = document.createElement('input');
//       idEl.type = 'hidden';
//       idEl.name = 'id';
//       dom.form.appendChild(idEl);
//       dom.hiddenIdInput = idEl;
//     }
//     if (idEl) idEl.value = variant ? String(variant.id) : '';
//   }
//   function setContinueEnabled(can) {
//     if (!dom.continueBtn) return;
//     dom.continueBtn.hidden = false;
//     dom.continueBtn.disabled = !can;
//   }
//   function setStep2TabEnabled(can) {
//     const tab = document.getElementById('tab-step2');
//     if (!tab) return;
//     tab.removeAttribute('tabindex');
//     tab.setAttribute('aria-selected', 'false');
//     if (can) tab.removeAttribute('aria-disabled');
//     else tab.setAttribute('aria-disabled', 'true');
//   }
//   function dispatchVariantChange(variant, previousVariant) {
//     try {
//       const formId = window.AGB_BOOT?.productFormId || window.AGB_FORM_ID;
//       const form = document.getElementById(formId);
//       const target = form || document;
//       if (!form) {
//         // Form not found for dispatch — using document
//       }

//       if (!variant) {
//         // Attempting to dispatch null/undefined variant
//         return;
//       }

//       // Ensure variant has all required properties that theme.js expects
//       const safeVariant = variant ? {
//         id: variant.id || null,
//         title: variant.title || '',
//         available: Boolean(variant.available),
//         price: variant.price || 0,
//         compare_at_price: variant.compare_at_price || 0,
//         sku: variant.sku || '',
//         option1: variant.option1 || '',
//         option2: variant.option2 || '',
//         option3: variant.option3 || '',
//         featured_media: variant.featured_media || variant.featured_image || null,
//         featured_image: variant.featured_image || null,
//         media: variant.media || (variant.featured_image ? [variant.featured_image] : []),
//         ...variant
//       } : null;

//       const product = Store.getState().product;
//       const safeProduct = product ? {
//         ...product,
//         media: product.media || (product.images ? product.images.map(img => ({ src: img })) : []),
//         featured_media: product.featured_media || product.featured_image || null
//       } : null;

//       target.dispatchEvent(new CustomEvent('variant:change', {
//         bubbles: true,
//         detail: {
//           product: safeProduct,
//           variant: safeVariant,
//           previousVariant: previousVariant || null
//         }
//       }));
//     } catch (e) {
//       // Failed to dispatch variant:change
//     }
//   }

//   // Public helper needed by Suggest
//   function pickVariantByColor(color) {
//     const p = Store.getState()?.product;
//     if (!p?.variants || !color) return null;
//     return p.variants.find(v => v.option1 === color && v.available)
//       || p.variants.find(v => v.option1 === color)
//       || null;
//   }

//   // ---- shared color fallback map -------------------------------------------
//   const COLOR_FALLBACK_MAP = {
//     'Multicam': ['Coyote Tan', 'Ranger Green', 'Coyote Brown'],
//     'Multicam Black': ['Black', 'Ranger Green'],
//     'Ranger Green': ['Multicam','Black', 'Coyote Tan'],
//     'Black': ['Multicam Black', 'Ranger Green'],
//     'M81 Woodland': ['Multicam', 'Ranger Green'],
//     'Coyote Brown': ['Coyote Tan', 'Multicam'],
//     'Coyote Tan': ['Coyote Brown', 'Multicam'],
//   };

//   // Update the current (pending) suggestion to match the base color, then re-render the card
//   async function updateCurrentSuggestionToColor(newBaseColor) {
//     const st = Store.getState();
//     if (!st?.pendingSuggestion?.handle || !newBaseColor) return;

//     // Try Suggest cache first (fast path)
//     let product = null;
//     try {
//       const Suggest = requireSuggest();
//       if (Suggest?.fetchProductData) {
//         product = await Promise.resolve(Suggest.fetchProductData(st.pendingSuggestion.handle));
//       }
//     } catch { }

//     // If cache miss, fetch once (rare)
//     if (!product) {
//       try {
//         const res = await fetch(`/products/${st.pendingSuggestion.handle}.js`);
//         if (res.ok) product = await res.json();
//       } catch { }
//     }
//     if (!product?.variants?.length) return;

//     // Choose best variant by color with limited fallbacks
//     const pick = chooseVariantForColor(product.variants, newBaseColor);
//     if (!pick) return;

//     // Update preview color and re-render the current card
//     Store.mutate(s => { if (s.pendingSuggestion) s.pendingSuggestion.color = pick.option1; });
//     Core.scheduler.mark('canvas');

//     try {
//       const Suggest = requireSuggest();
//       Suggest?.renderNext?.({ noAutoHop: true }); // redraw current suggestion card to reflect new color
//     } catch { }
//   }

//   // Update the currently visible suggestion card's image/price in-place
//   async function updateCurrentCardForColor(newColor) {
//     const { suggestionsList } = Core.dom || {};
//     const card = suggestionsList?.querySelector('.suggestion-card');
//     if (!card || !newColor) return;

//     const handle = card.dataset.handle;
//     if (!handle) return;

//     // cache-first
//     let product = null;
//     try { product = await Promise.resolve(requireSuggest()?.fetchProductData?.(handle)); } catch { }
//     if (!product?.variants?.length) {
//       try {
//         const res = await fetch(`/products/${handle}.js`);
//         if (res.ok) product = await res.json();
//       } catch { }
//     }
//     if (!product?.variants?.length) return;

//     const pick = chooseVariantForColor(product.variants, newColor);
//     if (!pick) return;

//     // Update DOM
//     const imgEl = card.querySelector('.suggestion-card-img');
//     const raw = pick.featured_image?.src || (product.images?.[0] || '');
//     const url = raw ? raw + (raw.includes('?') ? '&' : '?') + 'width=100' : '';
//     if (imgEl && url) imgEl.src = url;

//     // Update price
//     const priceEl = card.querySelector('.agb-item-price') || (() => {
//       const el = document.createElement('span');
//       el.className = 'agb-item-price';
//       const title = card.querySelector('.suggestion-card-title');
//       title?.insertAdjacentElement('afterend', el);
//       return el;
//     })();
//     const qty = Number(card.dataset.qty || 1);
//     Core.utils.renderCardPrice({
//       container: priceEl,
//       qty,
//       price: (Number(pick.price) || 0) / 100,
//       compareAt: (Number(pick.compare_at_price) || 0) / 100
//     });

//     // Keep dataset in sync
//     card.dataset.variantId = String(pick.id);
//     card.dataset.color = pick.option1;
//   }

//   // ---- public: resolve -----------------------------------------------------
//   function resolveVariant() {
//     const state = Store.getState();
//     const selected = getSelectedOptionValues();
//     const allChosen = selected.length === optionCount() && selected.every(Boolean);
//     // Resolve variant debug info

//     const variant = allChosen ? findMatchingVariant(selected) : null;

//     setHiddenId(variant);
//     // Require user interaction with ALL options, not just auto-selected values
//     const userChoseAllOptions = nav._allOptionsChosenByUser();
//     const canContinue = !!(variant && variant.available && userChoseAllOptions);
//     // Can continue debug info
//     setContinueEnabled(canContinue);
//     setStep2TabEnabled(canContinue);

//     // keep common color & a lightweight “selected variant” mirror in state
//     const colorIdx = findColorOptionIndex();
//     const color = colorIdx > -1 ? (selected[colorIdx] || null) : null;
//     const size = selected[1] || null;

//     Store.mutate(s => {
//       s.commonVariantColor.color = color;
//       s._selectedVariant = variant ? {
//         id: variant.id,
//         sku: variant.sku,
//         available: !!variant.available,
//         price: variant.price,
//         option1: variant.option1,
//         option2: variant.option2,
//         featured_image: variant.featured_image || null
//       } : null;
//     });

//     // tell theme/gallery & sticky — only when we have a real variant
//     if (variant) {
//       const previousVariant = _prevResolvedVariant;
//       dispatchVariantChange(variant, previousVariant);
//       _prevResolvedVariant = variant;
//     }

//     // If there’s already a base item, keep it in sync
//     // Replace the existing base item update section with:
//     if (state.baseItem && variant) {
//       const previousColor = state.baseItem.color;

//       Store.mutate(s => {
//         s.baseItem.id = variant.id;
//         s.baseItem.sku = variant.sku;
//         s.baseItem.color = variant.option1;
//         s.baseItem.size = variant.option2;
//         s.baseItem.price = (Number(variant.price) / 100).toFixed(2);
//         s.baseItem.available = !!variant.available;
//         s.baseItem.image = variant.featured_image?.src || s.product?.featured_image;
//       });

//       // Sync all (accepted + pending suggestion + suggestion card re-render)
//       if (previousColor !== variant.option1) {
//         syncAllToBaseColor(variant.option1);
//         updateCurrentCardForColor(variant.option1); // force repaint of current card
//       }

//       scheduler.mark('ui'); scheduler.mark('pricing'); scheduler.mark('sticky'); scheduler.mark('canvas');
//       ErrorHandler?.safeSync(() => window.DEBUG_LOGGER?.baseItemEdit?.(color, size), null, 'DEBUG_LOGGER.baseItemEdit');
//     }

//     // INTERNAL PROGRESSION: Only advance when strict gate passes (all options chosen by user & variant available)
//     if (!state.proceededToStep2 && state.builderStage === 1 && nav.gateStep2Strict()) {
//       // Strict gate passed
//       // Set base item from current selection if not already set
//       if (variant && state.product && !state.baseItem) {
//         const qtyInput = dom.form?.querySelector('input[name="quantity"]');
//         const qty = parseInt(qtyInput?.value || 1, 10);
//         const variantPriceUnits = Number(variant.price) / 100;

//         const newBaseItem = {
//           id: variant.id,
//           handle: state.product.handle,
//           title: state.product.title,
//           sku: variant.sku,
//           color: variant.option1,
//           size: variant.option2,
//           qty,
//           price: variantPriceUnits.toFixed(2),
//           available: variant.available,
//           image: variant.featured_image?.src || state.product.featured_image
//         };

//         // Set base item
//         Store.setBaseItem(newBaseItem);
//         scheduler.mark('ui'); scheduler.mark('pricing'); scheduler.mark('sticky'); scheduler.mark('canvas');
//       }

//       // Automatically progress to stage 2
//       setTimeout(() => {
//         // Calling nav.go(2)
//         nav.go(2);
//       }, 100);
//     }

//   }

//   // ---- public: sync base with current hidden id ----------------------------
//   function syncBaseItemVariant() {
//     const st = Store.getState();
//     if (!st.baseItem || !dom.form) return;
//     const id = dom.form.querySelector('input[name="id"]')?.value;
//     if (!id) return;

//     const v = st.product?.variants?.find(x => String(x.id) === String(id));
//     if (!v) return;

//     const previousColor = st.baseItem.color;

//     Store.mutate(s => {
//       s.baseItem.id = v.id;
//       s.baseItem.sku = v.sku;
//       s.baseItem.color = v.option1;
//       s.baseItem.size = v.option2;
//       s.baseItem.price = (Number(v.price) / 100).toFixed(2);
//       s.baseItem.available = !!v.available;
//       s.baseItem.image = v.featured_image?.src || s.product?.featured_image;
//       s.commonVariantColor.color = v.option1 || s.commonVariantColor.color;
//     });

//     // Sync all (accepted + pending suggestion + suggestion card re-render)
//     if (previousColor !== v.option1) {
//       syncAllToBaseColor?.(v.option1);
//     }

//     scheduler.mark('ui');
//     scheduler.mark('canvas');
//     updateCurrentCardForColor(v.option1);
//     updateCurrentSuggestionToColor(v.option1);
//   }

//   // Pick best available variant given preferred color with limited fallbacks
//   function chooseVariantForColor(variants, desiredColor) {
//     if (!Array.isArray(variants) || !desiredColor) return null;

//     // 1) exact available
//     let v = variants.find(v => v.option1 === desiredColor && v.available);
//     if (v) return v;

//     // 2) exact (even if not available) – do not use if unavailable; skip
//     // 3) try fallback colors if defined and available
//     const fallbacks = COLOR_FALLBACK_MAP[desiredColor];
//     if (Array.isArray(fallbacks)) {
//       for (const fallbackColor of fallbacks) {
//         v = variants.find(v => v.option1 === fallbackColor && v.available);
//         if (v) return v;
//       }
//     }

//     // 4) nothing suitable
//     return null;
//   }

//   // Sync accepted items and current suggestion to base color (fast)
//   async function syncAllToBaseColor(newBaseColor) {
//     const state = Store.getState();
//     if (!newBaseColor) return;

//     // ---- Accepted items: ensure every item is updated to the new color (with stock check) ----
//     let acceptedChanged = false;

//     // Build product cache for handles that lack embedded variants
//     const handlesNeedingProduct = new Set();
//     for (const it of (state.acceptedItems || [])) {
//       if (!Array.isArray(it.variants) || it.variants.length === 0) handlesNeedingProduct.add(it.handle);
//     }

//     const productByHandle = Object.create(null);
//     if (handlesNeedingProduct.size > 0) {
//       const Suggest = requireSuggest();
//       const fetchers = Array.from(handlesNeedingProduct).map(async (h) => {
//         let prod = null;
//         try { prod = await Promise.resolve(Suggest?.fetchProductData?.(h)); } catch { }
//         if (!prod) {
//           try {
//             const res = await fetch(`/products/${h}.js`);
//             if (res.ok) prod = await res.json();
//           } catch { }
//         }
//         if (prod && Array.isArray(prod.variants)) productByHandle[h] = prod;
//       });
//       await Promise.all(fetchers);
//     }

//     const updatedAccepted = (state.acceptedItems || []).map(item => {
//       // Prefer embedded variants; fallback to fetched product variants
//       const variants = Array.isArray(item.variants) && item.variants.length
//         ? item.variants
//         : (productByHandle[item.handle]?.variants || null);

//       if (!variants) return item; // cannot resolve; leave unchanged

//       const target = chooseVariantForColor(variants, newBaseColor);
//       // If no in-stock match (even after fallback), keep original item
//       if (!target) return item;

//       // Skip if already on the same variant id
//       if (String(target.id) === String(item.variantId)) return item;

//       acceptedChanged = true;
//       return {
//         ...item,
//         variantId: target.id,
//         id: target.id,
//         color: target.option1,
//         variantColor: target.option1,
//         price: (Number(target.price) / 100).toFixed(2),
//         title: `${(item.title || '').split(' (')[0]} (${target.title})`
//       };
//     });

//     if (acceptedChanged) {
//       Store.mutate(s => { s.acceptedItems = updatedAccepted; });
//       try { window.acceptedItems = updatedAccepted; } catch { }
//       scheduler.mark('ui'); scheduler.mark('sticky'); scheduler.mark('pricing'); scheduler.mark('canvas');
//       Core.bus.emit('acceptedItemsChanged');
//     }

//     // ---- Current suggestion (pendingSuggestion) ----
//     const ps = state.pendingSuggestion;
//     if (!ps?.handle) return;

//     // Try Suggest cache first
//     let product = null;
//     try {
//       const Suggest = requireSuggest();
//       if (Suggest?.fetchProductData) {
//         // normalize sync/async return to a promise
//         product = await Promise.resolve(Suggest.fetchProductData(ps.handle));
//       }
//     } catch { }

//     // If cache miss, fetch once (rare)
//     if (!product) {
//       try {
//         const res = await fetch(`/products/${ps.handle}.js`);
//         if (res.ok) product = await res.json();
//       } catch { }
//     }

//     if (!product?.variants?.length) return;

//     const pick = chooseVariantForColor(product.variants, newBaseColor);
//     if (!pick) return;

//     // Update color for preview and re-render the suggestion card
//     Store.mutate(s => { if (s.pendingSuggestion) s.pendingSuggestion.color = pick.option1; });
//     scheduler.mark('canvas');

//     try {
//       const Suggest = requireSuggest();
//       Suggest?.renderNext?.({ noAutoHop: true }); // repaint current card with new color
//     } catch { }
//   }

//   // Auto-sync when the base item changes (e.g., color change)
//   bus.on('baseItemChanged', () => {
//     const st = Store.getState();
//     const color = st.baseItem?.color;
//     if (!color) return;
//     // Orchestrate updates: accepted items, pending suggestion, card re-render, canvas
//     syncAllToBaseColor(color);
//     // Ensure the visible suggestion card reflects the new color immediately
//     updateCurrentCardForColor(color);
//   });

//   // ---- nav / gating --------------------------------------------------------
//   const nav = {
//     isStackedLayout() {
//       const bp = Number(Core?.config?.MOBILE_STACK_BREAKPOINT ?? 1000);
//       return window.innerWidth < bp;
//     },

//     // Strict completeness: every option has a value AND the user touched all options
//     _allOptionsChosenByUser() {
//       const n = optionCount();
//       if (n === 0) return false;

//       const selected = getSelectedOptionValues();
//       const interacted = Store.getState().userInteractedOptions || new Set();

//       const allFilled = selected.length === n && selected.every(Boolean);
//       const touchedAll = interacted.size >= n; // user actually interacted with each option

//       return allFilled && touchedAll;
//     },

//     // Match the current selection to a real, available variant
//     _getSelectedVariant() {
//       const st = Store.getState();
//       const id = Core.dom.form?.querySelector('input[name="id"]')?.value;
//       if (id) {
//         return st.product?.variants?.find(v => String(v.id) === String(id)) || null;
//       }
//       // Fallback to a pure match from current selections
//       return findMatchingVariant(getSelectedOptionValues());
//     },

//     _variantIsAvailable(v) {
//       return !!(v && v.available);
//     },

//     // Public: strict gate for Step 2
//     gateStep2Strict() {
//       const okByUser = this._allOptionsChosenByUser();
//       const v = this._getSelectedVariant();
//       const ok = okByUser && this._variantIsAvailable(v);
//       // Gate step 2 strict debug
//       return ok;
//     },

//     // Ensure baseItem is created/synced before step 2
//     _ensureBaseItemFromSelection() {
//       const st = Store.getState();
//       if (st.baseItem) return true;

//       const v = this._getSelectedVariant();
//       if (!v) return false;

//       const qty = parseInt(Core.dom.form?.querySelector('input[name="quantity"]')?.value || 1, 10);
//       Store.setBaseItem({
//         id: v.id,
//         handle: st.product?.handle,
//         title: st.product?.title,
//         sku: v.sku,
//         color: v.option1,
//         size: v.option2,
//         qty,
//         price: (Number(v.price) / 100).toFixed(2),
//         available: !!v.available,
//         image: v.featured_image?.src || st.product?.featured_image
//       });
//       return true;
//     },

//     async go(step) {
//       const st = Store.getState();
//       if (Number(step) !== 2) return;

//       // Strict gate
//       if (!this.gateStep2Strict()) {
//         Core.utils?.toast?.('Please choose color & size first.');
//         return;
//       }
//       if (!this._ensureBaseItemFromSelection()) return;

//       // INTERNAL progression: state is the source of truth
//       // Progressing to stage 2
//       Store.setBuilderStage(2);

//       try {
//         const Panels = Core.require?.('panels');
//         if (Panels?.manageCanvasGalleryVisibility) {
//           Panels.manageCanvasGalleryVisibility(true);
//           // Canvas shown, gallery hidden
//         }
//       } catch (e) {
//         // Failed to show canvas
//       }

//       if (!st.proceededToStep2) {
//         Store.mutate(s => { s.proceededToStep2 = true; });
//         document.documentElement.setAttribute('data-agb-step2', '1');
//         bus.emit('agb:step2');     // triggers lazy-loading & mobile rail opening
//         scheduler.mark('sticky');  // keep sticky in sync with stage change
//       }

//       // Open Step 2 panel - let agb-panels handle mobile rail opening via agb:step2 event
//       if (Core.dom.step2) {
//         // Always unhide step2 first to ensure it's available
//         Core.dom.step2.hidden = false;

//         // For desktop, show panel normally
//         // For mobile, the agb:step2 event will trigger agb-panels to handle rail opening
//         if (!this.isStackedLayout()) {
//           Core.dom.step2.classList.add('is-open');
//         }
//       }
//     }
//   };


//   return {
//     resolveVariant,
//     syncBaseItemVariant,
//     syncAllToBaseColor, // Add this
//     nav,
//     findColorOptionIndex,
//     getCurrentlySelectedColor() { // keep API your other modules expect
//       const idx = findColorOptionIndex();
//       if (idx < 0) return null;
//       return getSelectedOptionValues()[idx] || null;
//     },
//     pickVariantByColor,
//     updateCurrentCardForColor
//   };
// });
